import { Paper, IconButton, Grid, Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import MaskTextInput from "../../../components/MaskTextInput";
import { taxSlice } from "../../../store/reducer/tax";
import FileUploadIcon from "../../../assets/file_upload_icon.svg";
import EditIcon from "@mui/icons-material/Edit";
import {
  toDollar,
  toFloatString,
  toFloatStringAllowingNegative,
} from "../../../utils/dataConverter";
import { IRSSchedule1Form } from "../../../model/tax/irsschedule1";
import { TaxDocument } from "../../../model/tax/tax_document";
import { MaskTypes } from "../../../utils/models";
import InfoRounded from "@mui/icons-material/InfoRounded";
import { ValidatorForm } from "react-material-ui-form-validator";

interface ConfirmSchedule1Props {
  deleteFile: (index: number) => void;
  index: number;
  sch1: TaxDocument<IRSSchedule1Form>;
  editMode: boolean | undefined;
  toggleEditMode: (value: boolean) => void;
}

const ConfirmSchedule1 = ({
  deleteFile,
  index,
  sch1,
  editMode,
  toggleEditMode,
}: ConfirmSchedule1Props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (editMode === undefined) {
      toggleEditMode(true);
    }
  }, [editMode, toggleEditMode]);

  const renderStaticContent = () => {
    return (
      <>
        <div className="form-static">
          <label className="form-static-title">
            3 Business income or (loss)
          </label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.business_income_or_loss)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">6 Farm income or (loss)</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.farm_income_or_loss)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8b Gambling</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.gambling_income)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8c Cancellation of debt</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.cancellation_of_debt)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8h Jury duty pay</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.jury_duty_pay)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8j Activity not engaged in for profit income</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.activity_not_engaged_in_for_profit_income)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8l Income for the rental of personal property</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.income_for_the_rental_of_personal_property)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">
            8r Scholarship and grant income
          </label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.scholarship_and_grant_income)}
          </label>
        </div>
        <div className="form-static">
          <label className="form-static-title">8z Other income amount</label>
          <label className="form-static-value">
            {toDollar(sch1.form_data.other_income)}
          </label>
        </div>
      </>
    );
  };

  const renderEditFields = () => {
    return (
      <ValidatorForm onSubmit={() => { }}>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 6,
              md: 6
            }}>
            <MaskTextInput
              variant="outlined"
              name="business_income_or_loss"
              label="3 Business income or (loss)"
              placeholder="3 Business income or (loss)"
              className="text-field form-row"
              maskType={MaskTypes.Dollar}
              value={sch1.form_data.business_income_or_loss}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(
                  taxSlice.actions.updateSchedule1ValueChange({
                    index,
                    key: "business_income_or_loss",
                    value: toFloatStringAllowingNegative(e.target.value),
                  })
                )
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              md: 6
            }}>
            <MaskTextInput
              variant="outlined"
              name="farm_income_or_loss"
              label="6 Farm income or (loss)"
              placeholder="6 Farm income or (loss)"
              className="text-field form-row"
              maskType={MaskTypes.Dollar}
              value={sch1.form_data.farm_income_or_loss ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(
                  taxSlice.actions.updateSchedule1ValueChange({
                    index,
                    key: "farm_income_or_loss",
                    value: toFloatStringAllowingNegative(e.target.value),
                  })
                )
              }
            />
          </Grid>
        </Grid>
        <MaskTextInput
          variant="outlined"
          name="gambling_income"
          label="8b Gambling"
          placeholder="8b Gambling"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.gambling_income}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "gambling_income",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="cancellation_of_debt"
          label="8c Cancellation of debt"
          placeholder="8c Cancellation of debt"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.cancellation_of_debt}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "cancellation_of_debt",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="jury_duty_pay"
          label="8h Jury duty pay"
          placeholder="8h Jury duty pay"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.jury_duty_pay}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "jury_duty_pay",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="activity_not_engaged_in_for_profit_income"
          label="8j Activity not engaged in for profit income"
          placeholder="8j Activity not engaged in for profit income"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.activity_not_engaged_in_for_profit_income}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "activity_not_engaged_in_for_profit_income",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="income_for_the_rental_of_personal_property"
          label="8l Income for the rental of personal property"
          placeholder="8l Income for the rental of personal property"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.income_for_the_rental_of_personal_property}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "income_for_the_rental_of_personal_property",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="scholarship_and_grant_income"
          label="8r Scholarship and grant income"
          placeholder="8r Scholarship and grant income"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.scholarship_and_grant_income}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "scholarship_and_grant_income",
                value: toFloatString(e.target.value),
              })
            )
          }
        />
        <MaskTextInput
          variant="outlined"
          name="other_income"
          label="8z Other income amount"
          placeholder="8z Other income amount"
          className="text-field form-row"
          maskType={MaskTypes.Dollar}
          value={sch1.form_data.other_income}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch(
              taxSlice.actions.updateSchedule1ValueChange({
                index,
                key: "other_income",
                value: toFloatStringAllowingNegative(e.target.value),
              })
            )
          }
        />
      </ValidatorForm>
    );
  };

  return (
    <div className="file-info">
      <Paper elevation={0} className="file-wrapper">
        {(sch1.warnings ?? {}).uploaded_document?.map((warning: string, index: number) => (
          <Alert
            key={'alert-' + index}
            severity="error"
            variant="outlined"
            classes={{
              root: "error-alert",
              message: "success-msg",
              icon: "error-icon",
            }}
            iconMapping={{ error: <InfoRounded /> }}
          >
            {warning}
          </Alert>
        ))}
        <div className="uploaded-file-container">
          <p className="file-name-wrapper">
            <img
              src={FileUploadIcon}
              alt={"File upload icon"}
              className="file-img"
            />
            {sch1.uploaded_file.file_name}
          </p>
          <div className="review-link">
            {!editMode ? (
              <IconButton
                className="edit-btn"
                onClick={() => toggleEditMode(true)}
                size="large"
              >
                <EditIcon className="edit-icon" />
              </IconButton>
            ) : (
              <button className="delete-link" onClick={() => deleteFile(index)}>
                delete
              </button>
            )}
          </div>
        </div>

        <div className="file-title-header">
          <h3>Additional Income and Adjustments to Income</h3>
          <div className="spacer"></div>
          <button className={"delete-link"} onClick={() => deleteFile(index)}>
            delete
          </button>
        </div>
        {editMode ? renderEditFields() : renderStaticContent()}
      </Paper>
    </div>
  );
};

export default ConfirmSchedule1;
